import { useState, useRef, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useCamera } from '@pella/barcode-scanner';
import { useCreateRequest, useSelectionLists, useActionQuery, useDuplicateRequestCheck } from '../../../hooks';
import { downloadRef } from '../../../utils';

import {
  ModalRoot,
  Modal,
  ModalContent,
  ModalHeader,
  ModalFooter,
  Button,
  Incrementer,
  TextArea,
  Select,
  Icon,
  toast,
} from '@pella/sash-framework';
import { RequestBarcode } from '..';
import { DuplicateRequestModal } from '../../atoms';

import * as classes from './RequestModal.module.css';

const MIN_QUANTITY = 1;
const MAX_COMMENT_LENGTH = 100;

/**
 * Request Modal molecule
 * @param {Local.RequestModalProps} props - Component props
 */
export const RequestModal = ({ open, close, action }) => {
  const {
    id,
    description,
    message,
    showQuantity,
    showComment,
    showSelectionList,
    selectionListId1,
    selectionListId2,
    showDynamicInput,
    actionPrompt,
    actionQuery,
  } = action;
  const { mutate: createRequest, isPending } = useCreateRequest();
  const { data: allSelectionLists, isPending: isListsPending, error: listsError } = useSelectionLists();
  const {
    duplicateRequests,
    submitWithCheck,
    onCancel: onDuplicateCancel,
    onProceed: onDuplicateProceed,
    isChecking,
  } = useDuplicateRequestCheck(close);

  const { startCamera } = useCamera();

  const barcodeRef = useRef(null);

  const [formQuantity, setFormQuantity] = useState(MIN_QUANTITY);
  const [formComment, setFormComment] = useState('');
  const [dynamicInputValue, setDynamicInputValue] = useState('');
  const [formDynamicInput, setFormDynamicInput] = useState('');
  const [showBarcode, setShowBarcode] = useState(false);
  const dynamicInputTimerRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const [selectedListValueId1, setSelectedListValueId1] = useState(/** @type {string} */ (''));
  const [selectedListValueId2, setSelectedListValueId2] = useState(/** @type {string} */ (''));
  const {
    data: dynamicInputResult,
    isPending: isActionQueryPending,
    error: actionQueryError,
  } = useActionQuery({ id: actionQuery }, formDynamicInput);

  // cleanup ref on unmount
  useEffect(
    () => () => {
      if (dynamicInputTimerRef.current) clearTimeout(dynamicInputTimerRef.current);
    },
    [],
  );

  // Derived state for selection lists
  /** @type {Local.SelectionListWithValues | null | undefined} */
  const selectionList1 =
    showSelectionList && selectionListId1 ? allSelectionLists?.find((l) => l.id === selectionListId1) : null;
  /** @type {Local.SelectionListWithValues | null | undefined} */
  const selectionList2 =
    showSelectionList && selectionListId2 ? allSelectionLists?.find((l) => l.id === selectionListId2) : null;

  const selectionList1Values = selectionList1?.values || [];
  const selectionList2Values = selectionList2?.values || [];

  // Track if lists are ready: data loaded and all specified IDs found
  const listsLoaded = (allSelectionLists?.length ?? 0) > 0;
  const listsReady = listsLoaded && (!selectionListId1 || selectionList1) && (!selectionListId2 || selectionList2);

  // Track if a specified list ID wasn't found in the data
  const listNotFoundError = listsLoaded && !listsReady;

  /**
   * Build input object for mutation
   * @returns {Pella.ProductionRequestAPI.CreateRequestInput}
   */
  const buildInput = () => {
    /** @type {Pella.ProductionRequestAPI.CreateRequestInput} */
    const input = { actionId: id };

    if (showQuantity && formQuantity) input.requestQty = formQuantity;
    if (showComment && formComment) input.comment = formComment;
    if (showSelectionList && selectedListValueId1) input.dropDownListVal1 = selectedListValueId1;
    if (showSelectionList && selectedListValueId2) input.dropDownListVal2 = selectedListValueId2;
    if (showDynamicInput && formDynamicInput) input.dynamicInput = formDynamicInput;
    if (showDynamicInput && dynamicInputValue) input.dynamicInput = dynamicInputValue;
    if (showDynamicInput && dynamicInputResult) {
      input.dynamicInputResult = /** @type {Pella.ProductionRequestAPI.CreateRequestInput['dynamicInputResult']} */ (
        /** @type {unknown} */ (dynamicInputResult)
      );
    }
    return input;
  };

  /** Handle submit click, warning about open duplicate requests before calling the mutation */
  const onSubmitClick = () =>
    submitWithCheck(buildInput(), (input) => createRequest(input, { onSuccess: () => close() }));

  /**
   * Handle quantity change
   * Ensuring minimum is enforced as the Sash component is not properly doing so
   * @param {number} value
   */
  const onQuantityChange = (value) => setFormQuantity(Math.max(MIN_QUANTITY, value));

  /**
   * Handle comment change
   * @param {import('react').ChangeEvent<HTMLTextAreaElement>} event
   */
  const onCommentChange = ({ target: { value } }) => setFormComment(value);

  /**
   * Handle dynamic input change
   * Updates display value immediately; debounces the query-driving state by 500ms
   * @param {import('react').ChangeEvent<HTMLTextAreaElement>} event
   */
  const onDynamicInputChange = ({ target: { value } }) => {
    setDynamicInputValue(value);
    if (dynamicInputTimerRef.current) clearTimeout(dynamicInputTimerRef.current);
    dynamicInputTimerRef.current = setTimeout(() => setFormDynamicInput(value), 500);
  };

  /** Scan a barcode into the dynamic input field. Rejects text exceeding the max length. */
  const handleScanDynamicInput = () => {
    startCamera(
      (result) => {
        if (result.length > MAX_COMMENT_LENGTH) {
          toast.error(`Scanned text exceeds the ${MAX_COMMENT_LENGTH}-character limit.`);
          return;
        }
        setDynamicInputValue(result);
        setFormDynamicInput(result);
      },
      () => {
        // Camera dismissed — no action needed, user returns to the modal with the field untouched
      },
    );
  };

  /** Scan a barcode into the quantity field. Requires a canonical integer >= MIN_QUANTITY. */
  const handleScanQuantity = () => {
    startCamera(
      (result) => {
        const trimmed = result.trim();
        const parsed = Number(trimmed);
        const isValidQuantity = Number.isInteger(parsed) && parsed >= MIN_QUANTITY && String(parsed) === trimmed;
        if (!isValidQuantity) {
          toast.error('The scanned barcode does not contain a valid quantity.');
          return;
        }
        setFormQuantity(parsed);
      },
      () => {
        // Camera dismissed — no action needed, user returns to the modal with the field untouched
      },
    );
  };

  /** Scan a barcode into the comment field. Rejects text exceeding the max length. */
  const handleScanComment = () => {
    startCamera(
      (result) => {
        if (result.length > MAX_COMMENT_LENGTH) {
          toast.error(`Scanned text exceeds the ${MAX_COMMENT_LENGTH}-character limit.`);
          return;
        }
        setFormComment(result);
      },
      () => {
        // Camera dismissed — no action needed, user returns to the modal with the field untouched
      },
    );
  };

  /**
   * Determine if submit button should be disabled
   * Selection lists and dynamic input are required if their respective flags are true
   */
  const actionQueryFailed = showDynamicInput && Boolean(actionQueryError);
  const isSubmitDisabled =
    isPending ||
    isChecking ||
    Boolean(
      (showSelectionList &&
        (isListsPending ||
          Boolean(listsError) ||
          listNotFoundError ||
          !listsReady ||
          !selectedListValueId1 ||
          (selectionListId2 && !selectedListValueId2))) ||
      (showDynamicInput && !formDynamicInput) ||
      (showDynamicInput && (isActionQueryPending || actionQueryFailed || !dynamicInputValue)),
    );

  const showModalContent = showQuantity || showComment || showSelectionList || showDynamicInput;

  /** Handle show barcode toggle */
  const handleShowBarcode = () => setShowBarcode((prev) => !prev);

  /** Handle download of barcode as image */
  const handleDownload = async () => {
    await downloadRef(barcodeRef);
    setShowBarcode(false);
  };

  return (
    <>
      <ModalRoot open={open}>
        <Modal effect='scale-up' showClose={false} fullScreen={isMobile} className={classes.modal}>
          {!showBarcode && (
            <ModalHeader className={classes.header}>
              <div>{description}</div>
              <div>{message}</div>
            </ModalHeader>
          )}
          {showModalContent && !showBarcode && (
            <ModalContent>
              {showDynamicInput && !actionPrompt && (
                <div className={classes.listError}>
                  Error: Dynamic input action is missing required &quot;Action Prompt&quot; label. Please contact
                  support.
                </div>
              )}
              {showDynamicInput && actionQueryError && (
                <div className={classes.listError}>
                  {typeof actionQueryError === 'string'
                    ? actionQueryError
                    : actionQueryError?.message || String(actionQueryError)}
                </div>
              )}
              {showDynamicInput && actionPrompt && (
                <div className={classes.fieldWithScan}>
                  <div className={classes.dynamicInput}>
                    <div className={classes.charCount}>
                      {dynamicInputValue.length} / {MAX_COMMENT_LENGTH}
                    </div>
                    <TextArea
                      id='request-modal-dynamic-input'
                      labelText={actionPrompt}
                      rows={1}
                      value={dynamicInputValue}
                      maxLength={MAX_COMMENT_LENGTH}
                      onChange={onDynamicInputChange}
                    />
                  </div>
                  <button
                    type='button'
                    className={classes.scanButton}
                    aria-label='Scan barcode for input'
                    data-testid='dynamic-input-scan-button'
                    onClick={handleScanDynamicInput}
                  >
                    <Icon name='qr_code_2_add' />
                  </button>
                </div>
              )}
              {showQuantity && (
                <div className={classes.fieldWithScan}>
                  <Incrementer
                    id='request-modal-incrementer'
                    labelText='Quantity'
                    maxValue={Infinity}
                    minValue={MIN_QUANTITY}
                    onChange={onQuantityChange}
                    value={formQuantity}
                  />
                  <button
                    type='button'
                    className={classes.scanButton}
                    aria-label='Scan barcode for quantity'
                    data-testid='quantity-scan-button'
                    onClick={handleScanQuantity}
                  >
                    <Icon name='qr_code_2_add' />
                  </button>
                </div>
              )}
              {showSelectionList && (
                <>
                  {isListsPending && <div className={classes.listMessage}>Loading selection lists...</div>}
                  {listsError && (
                    <div className={classes.listError}>Error loading selection lists. Please try again.</div>
                  )}
                  {listNotFoundError && (
                    <div className={classes.listError}>Selection list not found. Please contact support.</div>
                  )}
                  {selectionList1 && (
                    <Select
                      id='selectionList1Select'
                      labelText={selectionList1.description}
                      value={selectedListValueId1}
                      onChange={(e) => setSelectedListValueId1(e.target.value)}
                      className={classes.selectionList}
                    >
                      <option value=''>--- Select an Option ---</option>
                      {selectionList1Values
                        .filter((value) => Boolean(value?.id))
                        .map((value) => (
                          <option key={value.id} value={value.description}>
                            {value.description}
                          </option>
                        ))}
                    </Select>
                  )}
                  {selectionList2 && (
                    <Select
                      id='selectionList2Select'
                      labelText={selectionList2.description}
                      value={selectedListValueId2}
                      onChange={(e) => setSelectedListValueId2(e.target.value)}
                      className={classes.selectionList}
                    >
                      <option value=''>--- Select an Option ---</option>
                      {selectionList2Values
                        .filter((value) => Boolean(value?.id))
                        .map((value) => (
                          <option key={value.id} value={value.description}>
                            {value.description}
                          </option>
                        ))}
                    </Select>
                  )}
                </>
              )}
              {showComment && (
                <div className={classes.fieldWithScan}>
                  <div className={classes.commentWrapper}>
                    <div className={classes.charCount}>
                      {formComment.length} / {MAX_COMMENT_LENGTH}
                    </div>
                    <TextArea
                      id='request-modal-comment'
                      labelText='Comment'
                      placeholder='Add any comments here'
                      rows={3}
                      value={formComment}
                      maxLength={MAX_COMMENT_LENGTH}
                      onChange={onCommentChange}
                    />
                  </div>
                  <button
                    type='button'
                    className={classes.scanButton}
                    aria-label='Scan barcode for comment'
                    data-testid='comment-scan-button'
                    onClick={handleScanComment}
                  >
                    <Icon name='qr_code_2_add' />
                  </button>
                </div>
              )}
            </ModalContent>
          )}
          {showBarcode && (
            <ModalContent>
              <RequestBarcode input={buildInput()} action={action} ref={barcodeRef} />
            </ModalContent>
          )}
          <ModalFooter className={classes.footer}>
            {!showBarcode && (
              <div className={classes.cancel}>
                <Button color='secondary' className={classes.button} onClick={close}>
                  Cancel
                </Button>
              </div>
            )}
            <Button
              color='secondary'
              className={classes.button}
              onClick={handleShowBarcode}
              disabled={isSubmitDisabled}
            >
              {showBarcode ? 'Back to Request' : 'Generate Barcode'}
            </Button>
            {!showBarcode && (
              <Button color='primary' className={classes.button} onClick={onSubmitClick} disabled={isSubmitDisabled}>
                Submit Request
              </Button>
            )}
            {showBarcode && (
              <Button color='primary' className={classes.button} onClick={handleDownload}>
                Download
              </Button>
            )}
          </ModalFooter>
        </Modal>
      </ModalRoot>
      <DuplicateRequestModal
        isOpen={duplicateRequests.length > 0}
        requests={duplicateRequests}
        onCancel={onDuplicateCancel}
        onProceed={onDuplicateProceed}
      />
    </>
  );
};
